import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Body from '../Components/layout/Body';
import { SELECTION_METHOD_BUTTONS, getSelectionMethodBtnsForValues, LOWTECH_SELECTION_METHOD_BUTTONS, getLowTechSelectionMethodBtnsForValues } from './ConstSelectionMethodBtns';
import { SUBTABS } from './ConstPaths';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import { OTHER_CKBX_VAL, isOtherChecked } from './OtherCheckboxUtils';
import OtherTextInput from '../Components/layout/OtherTextInput';
import { AM_CATEGORIES } from './ConstAccessMethodsMock';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_SETUP_SELMETHOD_HELP = `
  <div>
    <p>By “item selection method”, we mean how the client will select the item they want.  
    <strong>For example</strong>, if using a mouse, a typical item selection method is to click the mouse button.  
    But it is also possible to connect a separate switch to the mouse, to be used instead of the button, if that is easier or more efficient.   
    </p>
    <p>
    <strong>Note:</strong> The options on this screen will vary somewhat depending on the access method you’ve selected.  
    If you haven’t selected an access method already, you’ll see all possible options. 
    (And if you select a keyboard, you’ll skip this page, since we’ll know that the item selection method is direct selection.) 
    </p>
    <p><strong>In the low/no-tech case,</strong> you have two basic choices. One is direct selection. An example is looking directly at the target word on a communication board. Or pressing a switch to activate a call chime. 
    The second basic method is partner-assisted scanning. An example is where a partner points to a series of items, like letters, one at a time. The client gives a signal when the partner presents the desired letter.
    See <a href="https://assistivetech.barnsleyhospital.nhs.uk/resource/using-partner-assisted-scanning-on-a-letterboard-for-communication/" target="_blank">this video</a> for more about partner-assisted scanning on a letter board. The partner-assisted scanning approach can also be used for making other types of choices.</p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  isLowTechPath: PropTypes.bool,
  availableMethods: PropTypes.arrayOf(PropTypes.string),
  activeAccessMethodName: PropTypes.string,
  activeAccessMethodCategory: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupName: PropTypes.string,
  onChangeActiveSetupSelectionMethod: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  isLowTechPath: false,
  availableMethods: [],
  activeAccessMethodName: '',
  activeAccessMethodCategory: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupAccessories: [],
  activeSetupName: '',
};

//content sub-component with unique content for this page
class SetupSelectionMethodContent extends Component {
  constructor(props){
    super(props);
  }

  //Render the form.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the priority activity in the parent, 
  //which triggers a new render of the view with the new priority activity.

  render(){
    const {
      isLowTechPath,
      activeAccessMethodName,
      activeAccessMethodCategory,
      availableMethods,
      activeSetupSelectionMethod,
      onChangeActiveSetupSelectionMethod,
      otherSelectionMethod,
      onChangeOtherSelectionMethod
    } = this.props;
  
    const amPath = SUBTABS.CHOOSE;

    //figure out what selection methods are relevant, and get the objects to present those buttons
    let availableBtns = [];
    if(isLowTechPath || (activeAccessMethodCategory === AM_CATEGORIES.NO_TECH)){
      availableBtns = getLowTechSelectionMethodBtnsForValues(availableMethods); 
    }
    else{
      availableBtns = getSelectionMethodBtnsForValues(availableMethods);
    }
    let otherChecked = isOtherChecked(activeSetupSelectionMethod);

    //if we don't have any matching buttons, just use all of the low or regular tech buttons.
    //most likely reason for this is that no access method has been selected, or the method has no selectionMethods defined.
    if(availableMethods.length === 0){
      availableBtns = (isLowTechPath) ? LOWTECH_SELECTION_METHOD_BUTTONS : SELECTION_METHOD_BUTTONS;
    }

    let infoText = "There are different ways to select the item you want, depending on the access method you're using.";
    if(activeAccessMethodName){
      if (isLowTechPath){ //low tech and known access method
        infoText = "For low-tech activities, there are 2 main ways to select the item you want."
      }
      else{ //regular tech and known access method
        infoText = "For this access method, there may be several different ways to select the item that you want.";
      }
    }
    //if there is no active access method, it either shows all possible regular tech selection methods, or all the low-tech selection methods
    //(this is consistent and easier than also refining the selection methods based on low/regular tech)

    return(
      <>
      <p>{infoText}</p>
      <Form>
        <FormGroup tag="fieldset">
          <legend>Choose the one you plan to use:</legend>
          {availableBtns.map((btn, index) =>
            <React.Fragment key={index}>
            <FormGroup check key={index}>
              <Input
                id={btn.value}
                value={btn.value}
                checked={(activeSetupSelectionMethod.startsWith(btn.value))}
                onChange={onChangeActiveSetupSelectionMethod}
                name="selection-method"
                type="radio"
              />
              {' '}
              <Label for={btn.value} check>
                {btn.label}
                {' '}
                <span className="aa-more-info">{btn.moreInfo}</span>
              </Label>
            </FormGroup>
            {/* If the btn is 'other', include a text field to specify */}
            { ((btn.value === OTHER_CKBX_VAL)) 
              ?
                <OtherTextInput
                  otherChecked={otherChecked}
                  otherText={otherSelectionMethod}
                  onChangeOtherText={onChangeOtherSelectionMethod}
                />
              : null
            }
            </React.Fragment>
          )}
        </FormGroup>
      </Form>
      {(!activeAccessMethodName && !isLowTechPath) ?
        <p className="aa-more-info">NOTE: If you <Link to={amPath}>select an access method</Link>, we can offer more specific choices that relate to that method.</p>
      : null
      }
      </>              
    );
  }
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component.
//since the main goal step includes a form, receive the initial form values via props,
//along with setters to change those values based on the form input.
function TestDriveSetupSelectionMethod(props) {
  const {
    firstName,
    isLowTechPath,
    activeAccessMethodName,
    activeAccessMethodCategory,
    availableMethods,
    activeSetupBodySite,
    activeSetupAccessories,
    activeSetupName,
    activeSetupSelectionMethod,
    onChangeActiveSetupSelectionMethod,
    otherSelectionMethod,
    onChangeOtherSelectionMethod,
    saveFormData,
    onContinue
  } = props;

  const dev = props.dev || {};

  console.log("rendering TestDriveSetupSelectionMethod, method: " + activeSetupSelectionMethod);

  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used
    faIcon: "fa-gears",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `What item selection method will ${firstName} use?`,
    subTitle: "",
    learnMore: {linkText: "Learn More about item selection methods",
                title: "Item Selection Methods",
                content: TD_SETUP_SELMETHOD_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('TDSSelectionMethod mounted');
    return () => {
      console.log('TDSSelectionMethod unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={<SetupSelectionMethodContent 
        isLowTechPath={isLowTechPath}
        activeAccessMethodName={activeAccessMethodName} 
        activeAccessMethodCategory={activeAccessMethodCategory}
        availableMethods={availableMethods} 
        activeSetupSelectionMethod={activeSetupSelectionMethod} 
        onChangeActiveSetupSelectionMethod={onChangeActiveSetupSelectionMethod}
        otherSelectionMethod={otherSelectionMethod}
        onChangeOtherSelectionMethod={onChangeOtherSelectionMethod}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        :   
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
       />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupSelectionMethod.propTypes = propTypes;
TestDriveSetupSelectionMethod.defaultProps = defaultProps;

export default TestDriveSetupSelectionMethod;